/* ==========================================================================
   ToeAlign Pro — shared JavaScript
   Handles: media slots (click-to-upload), video embeds, cart, navigation,
   tabs, accordion, before/after slider, countdown
   ========================================================================== */

(function () {
  'use strict';

  /* ==========================================================================
     Toast helper
     ========================================================================== */
  function showToast(message) {
    let toast = document.getElementById('toaster');
    if (!toast) {
      toast = document.createElement('div');
      toast.id = 'toaster';
      toast.className = 'toast';
      document.body.appendChild(toast);
    }
    toast.textContent = message;
    toast.classList.add('show');
    clearTimeout(toast._t);
    toast._t = setTimeout(() => toast.classList.remove('show'), 2200);
  }

  /* ==========================================================================
     MEDIA SLOTS — click-to-upload images & video embeds
     Persists in localStorage so uploads survive page reloads
     ========================================================================== */
  function initMediaSlots() {
    const slots = document.querySelectorAll('.media-slot');
    slots.forEach(slot => {
      const slotId = slot.dataset.slot;
      const type = slot.dataset.type || 'image'; // image | video
      if (!slotId) return;

      // Build internal markup if not already present
      if (!slot.querySelector('.media-empty')) {
        const empty = document.createElement('div');
        empty.className = 'media-empty';
        empty.innerHTML = type === 'video'
          ? `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"><polygon points="5 3 19 12 5 21 5 3"/></svg>
             <span class="lbl">Click to add video</span>
             <span class="sub">${slot.dataset.label || 'Paste a YouTube or Vimeo URL'}</span>
             <span class="dim">${slot.dataset.dim || 'Recommended: 1920 × 1080'}</span>`
          : `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"><rect x="3" y="3" width="18" height="18" rx="2"/><circle cx="8.5" cy="8.5" r="1.5"/><path d="m21 15-5-5L5 21"/></svg>
             <span class="lbl">Click to add image</span>
             <span class="sub">${slot.dataset.label || ''}</span>
             <span class="dim">${slot.dataset.dim || 'Square / 1:1 recommended'}</span>`;
        slot.appendChild(empty);
      }

      const empty = slot.querySelector('.media-empty');
      const controls = document.createElement('div');
      controls.className = 'media-controls';
      controls.innerHTML = `
        <button class="media-ctrl" data-action="replace">Replace</button>
        <button class="media-ctrl" data-action="remove">Remove</button>
      `;
      slot.appendChild(controls);

      // Restore saved media
      const saved = localStorage.getItem('media-' + slotId);
      if (saved) {
        try {
          const data = JSON.parse(saved);
          if (data.type === 'image') {
            renderImage(slot, data.value);
          } else if (data.type === 'video-url') {
            renderVideoUrl(slot, data.value);
          } else if (data.type === 'video-file') {
            renderVideoFile(slot, data.value);
          }
        } catch (e) { /* corrupt entry — ignore */ }
      }

      // Click on empty area → trigger appropriate input
      empty.addEventListener('click', (e) => {
        e.stopPropagation();
        if (type === 'video') {
          openVideoPrompt(slot, slotId);
        } else {
          openFilePicker(slot, slotId, 'image');
        }
      });

      // Replace / Remove buttons
      controls.addEventListener('click', (e) => {
        const action = e.target.dataset.action;
        if (action === 'replace') {
          if (type === 'video') openVideoPrompt(slot, slotId);
          else openFilePicker(slot, slotId, 'image');
        } else if (action === 'remove') {
          localStorage.removeItem('media-' + slotId);
          slot.classList.remove('has-media');
          const wrap = slot.querySelector('.media-preview, .video-wrap');
          if (wrap) wrap.remove();
          showToast('Removed');
        }
      });
    });
  }

  function openFilePicker(slot, slotId, accept) {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = accept === 'video' ? 'video/*' : 'image/*';
    input.style.display = 'none';
    document.body.appendChild(input);

    input.onchange = () => {
      const file = input.files && input.files[0];
      if (!file) { input.remove(); return; }
      const reader = new FileReader();
      reader.onload = (ev) => {
        const dataUrl = ev.target.result;
        try {
          if (accept === 'image') {
            localStorage.setItem('media-' + slotId, JSON.stringify({ type: 'image', value: dataUrl }));
            renderImage(slot, dataUrl);
          } else {
            localStorage.setItem('media-' + slotId, JSON.stringify({ type: 'video-file', value: dataUrl }));
            renderVideoFile(slot, dataUrl);
          }
          showToast('Saved');
        } catch (err) {
          showToast('File too large for browser storage');
        }
      };
      reader.readAsDataURL(file);
      input.remove();
    };
    input.click();
  }

  function openVideoPrompt(slot, slotId) {
    // First ask: paste URL or upload file?
    const choice = window.prompt(
      'Paste a YouTube/Vimeo URL,\nor leave empty and click OK to upload a video file:',
      ''
    );
    if (choice === null) return; // cancelled

    if (choice.trim() === '') {
      // upload file path
      openFilePicker(slot, slotId, 'video');
    } else {
      const embed = toEmbedUrl(choice.trim());
      if (!embed) {
        alert('Sorry, could not recognise that URL. Use a YouTube or Vimeo link.');
        return;
      }
      localStorage.setItem('media-' + slotId, JSON.stringify({ type: 'video-url', value: embed }));
      renderVideoUrl(slot, embed);
      showToast('Video embedded');
    }
  }

  function toEmbedUrl(url) {
    // YouTube
    const yt = url.match(/(?:youtube\.com\/(?:watch\?v=|embed\/|shorts\/)|youtu\.be\/)([A-Za-z0-9_-]{6,})/);
    if (yt) return 'https://www.youtube.com/embed/' + yt[1];
    // Vimeo
    const vm = url.match(/vimeo\.com\/(?:video\/)?(\d+)/);
    if (vm) return 'https://player.vimeo.com/video/' + vm[1];
    return null;
  }

  function renderImage(slot, dataUrl) {
    let img = slot.querySelector('.media-preview');
    if (!img) {
      img = document.createElement('img');
      img.className = 'media-preview';
      slot.appendChild(img);
    }
    img.src = dataUrl;
    slot.classList.add('has-media');
  }

  function renderVideoUrl(slot, url) {
    let wrap = slot.querySelector('.video-wrap');
    if (!wrap) {
      wrap = document.createElement('div');
      wrap.className = 'video-wrap';
      slot.appendChild(wrap);
    }
    wrap.innerHTML = `<iframe src="${url}" allow="autoplay; fullscreen; picture-in-picture" allowfullscreen></iframe>`;
    slot.classList.add('has-media');
  }

  function renderVideoFile(slot, dataUrl) {
    let wrap = slot.querySelector('.video-wrap');
    if (!wrap) {
      wrap = document.createElement('div');
      wrap.className = 'video-wrap';
      slot.appendChild(wrap);
    }
    wrap.innerHTML = `<video src="${dataUrl}" controls playsinline></video>`;
    slot.classList.add('has-media');
  }

  /* ==========================================================================
     CART (localStorage-based, headless-ready)
     ========================================================================== */
  const Cart = {
    KEY: 'tap-cart',
    get() {
      try { return JSON.parse(localStorage.getItem(this.KEY)) || []; }
      catch (e) { return []; }
    },
    set(items) {
      localStorage.setItem(this.KEY, JSON.stringify(items));
      this.render();
    },
    add(item) {
      const items = this.get();
      const ix = items.findIndex(i => i.id === item.id);
      if (ix >= 0) items[ix].qty += item.qty || 1;
      else items.push(Object.assign({ qty: 1 }, item));
      this.set(items);
      showToast('Added to cart');
    },
    remove(id) {
      this.set(this.get().filter(i => i.id !== id));
    },
    updateQty(id, qty) {
      const items = this.get();
      const ix = items.findIndex(i => i.id === id);
      if (ix >= 0) {
        items[ix].qty = Math.max(1, qty);
        this.set(items);
      }
    },
    count() { return this.get().reduce((s, i) => s + i.qty, 0); },
    subtotal() { return this.get().reduce((s, i) => s + i.qty * i.price, 0); },
    render() {
      // update header bubbles
      document.querySelectorAll('[data-cart-count]').forEach(el => {
        el.textContent = this.count();
      });
      // re-render cart page if present
      if (typeof renderCartPage === 'function') renderCartPage();
    }
  };
  window.Cart = Cart; // expose globally

  /* ==========================================================================
     Mobile nav toggle
     ========================================================================== */
  function initNavToggle() {
    const btn = document.querySelector('.menu-toggle');
    const links = document.querySelector('.nav-links');
    if (btn && links) {
      btn.addEventListener('click', () => {
        links.classList.toggle('open');
      });
    }
  }

  /* ==========================================================================
     Smooth scroll to anchors
     ========================================================================== */
  function initSmoothScroll() {
    document.querySelectorAll('a[href^="#"]').forEach(a => {
      a.addEventListener('click', function (e) {
        const id = this.getAttribute('href');
        if (id.length > 1) {
          const el = document.querySelector(id);
          if (el) {
            e.preventDefault();
            const y = el.getBoundingClientRect().top + window.scrollY - 76;
            window.scrollTo({ top: y, behavior: 'smooth' });
          }
        }
      });
    });
  }

  /* ==========================================================================
     PDP TABS (registers)
     ========================================================================== */
  function initPdpTabs() {
    const nav = document.querySelector('.pdp-tabs-nav');
    if (!nav) return;
    const buttons = nav.querySelectorAll('.pdp-tab-btn');
    const panels = document.querySelectorAll('.pdp-tab-panel');
    buttons.forEach(btn => {
      btn.addEventListener('click', () => {
        const target = btn.dataset.tab;
        buttons.forEach(b => b.classList.toggle('active', b === btn));
        panels.forEach(p => p.classList.toggle('active', p.dataset.tab === target));
      });
    });
  }

  /* ==========================================================================
     Accordion (FAQ)
     ========================================================================== */
  function initFaqCategories() {
    const cats = document.querySelectorAll('.faq-cat');
    if (!cats.length) return;
    cats.forEach(c => {
      c.addEventListener('click', () => {
        cats.forEach(x => x.classList.toggle('active', x === c));
        const cat = c.dataset.cat;
        document.querySelectorAll('.qa').forEach(qa => {
          qa.style.display = (cat === 'all' || qa.dataset.cat === cat) ? '' : 'none';
        });
      });
    });
  }

  /* ==========================================================================
     PDP gallery (thumbs → main)
     ========================================================================== */
  function initPdpGallery() {
    const thumbs = document.querySelectorAll('.pdp-thumb');
    const mainSlot = document.querySelector('.pdp-main');
    if (!thumbs.length || !mainSlot) return;
    thumbs.forEach(thumb => {
      thumb.addEventListener('click', () => {
        if (thumb.classList.contains('media-slot') && !thumb.classList.contains('has-media')) {
          // it's an upload slot — let the slot handler trigger
          return;
        }
        thumbs.forEach(t => t.classList.toggle('active', t === thumb));
        const slotId = thumb.dataset.linkSlot;
        if (!slotId) return;
        const saved = localStorage.getItem('media-' + slotId);
        if (saved) {
          try {
            const data = JSON.parse(saved);
            const previewImg = mainSlot.querySelector('.media-preview');
            if (data.type === 'image' && previewImg) {
              previewImg.src = data.value;
            }
          } catch (e) {}
        }
      });
    });
  }

  /* ==========================================================================
     Bundle / pricing radio
     ========================================================================== */
  function initBundleSelector() {
    const opts = document.querySelectorAll('.price-option');
    if (!opts.length) return;
    opts.forEach(o => {
      o.addEventListener('click', e => {
        e.preventDefault();
        opts.forEach(x => x.classList.toggle('active', x === o));
        // update CTA price display
        const price = parseFloat(o.dataset.price);
        document.querySelectorAll('[data-bundle-price]').forEach(el => {
          el.textContent = '$' + price.toFixed(2);
        });
      });
    });
  }

  /* ==========================================================================
     Add-to-cart buttons
     ========================================================================== */
  function initAddToCart() {
    document.querySelectorAll('[data-add-to-cart]').forEach(btn => {
      btn.addEventListener('click', e => {
        e.preventDefault();
        const active = document.querySelector('.price-option.active');
        const id = (active && active.dataset.id) || btn.dataset.id || 'toealign-pro';
        const name = (active && active.dataset.name) || btn.dataset.name || 'ToeAlign Pro';
        const price = parseFloat((active && active.dataset.price) || btn.dataset.price || '59.90');
        Cart.add({ id, name, price, qty: 1 });
      });
    });
  }

  /* ==========================================================================
     Before / after slider
     ========================================================================== */
  function initBeforeAfter() {
    document.querySelectorAll('.ba-wrap').forEach(wrap => {
      const handle = wrap.querySelector('.ba-handle');
      const after = wrap.querySelector('.ba-after');
      if (!handle || !after) return;
      let dragging = false;
      function move(x) {
        const rect = wrap.getBoundingClientRect();
        const pct = Math.max(0, Math.min(100, ((x - rect.left) / rect.width) * 100));
        handle.style.left = pct + '%';
        after.style.clipPath = `inset(0 0 0 ${pct}%)`;
      }
      wrap.addEventListener('pointerdown', e => { dragging = true; move(e.clientX); wrap.setPointerCapture(e.pointerId); });
      wrap.addEventListener('pointermove', e => { if (dragging) move(e.clientX); });
      wrap.addEventListener('pointerup', e => { dragging = false; });
      wrap.addEventListener('pointercancel', () => { dragging = false; });
    });
  }

  /* ==========================================================================
     Countdown timer
     ========================================================================== */
  function initCountdown() {
    const el = document.querySelector('[data-countdown-to]');
    if (!el) return;
    const target = new Date(el.dataset.countdownTo).getTime();
    function tick() {
      const now = Date.now();
      const distance = Math.max(0, target - now);
      const days = Math.floor(distance / 86400000);
      const hours = Math.floor((distance % 86400000) / 3600000);
      const minutes = Math.floor((distance % 3600000) / 60000);
      const seconds = Math.floor((distance % 60000) / 1000);
      const pad = n => (n < 10 ? '0' : '') + n;
      const d = el.querySelector('[data-d]');
      const h = el.querySelector('[data-h]');
      const m = el.querySelector('[data-m]');
      const s = el.querySelector('[data-s]');
      if (d) d.textContent = pad(days);
      if (h) h.textContent = pad(hours);
      if (m) m.textContent = pad(minutes);
      if (s) s.textContent = pad(seconds);
    }
    tick();
    setInterval(tick, 1000);
  }

  /* ==========================================================================
     Quantity stepper
     ========================================================================== */
  function initQty() {
    document.querySelectorAll('.qty-stepper').forEach(stepper => {
      const input = stepper.querySelector('input');
      const minus = stepper.querySelector('[data-qty-minus]');
      const plus = stepper.querySelector('[data-qty-plus]');
      if (minus) minus.addEventListener('click', () => {
        input.value = Math.max(1, parseInt(input.value) - 1);
      });
      if (plus) plus.addEventListener('click', () => {
        input.value = parseInt(input.value) + 1;
      });
    });
  }

  /* ==========================================================================
     Init on DOM ready
     ========================================================================== */
  function init() {
    initMediaSlots();
    initNavToggle();
    initSmoothScroll();
    initPdpTabs();
    initFaqCategories();
    initPdpGallery();
    initBundleSelector();
    initAddToCart();
    initBeforeAfter();
    initCountdown();
    initQty();
    Cart.render();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
